import React, { useEffect, useState } from 'react';
import { useCreatingNewUserAtom } from '../../atoms/users/useCreatingNewUserAtom';
import { useUserToEditAtom } from '../../atoms/users/useUserToEditAtom';
import { updateUser, deleteUser, reactivateUser } from '../../api/authenticateAPIs';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useNotification } from '../../context/NotificationContext';
import { NotificationType } from '../../enums/NotificationType';
import useAuth from '../../hooks/useAuth';
import ConfirmDeleteModal from '../../components/shared/ConfirmDeleteModal';
import RoleSelectBox from '../../components/shared/RoleSelectBox';
import AllTeamsMultiselect from '../../components/shared/AllTeamsMultiselect';
import PasswordGenerateButton from '../../components/shared/PasswordGenerateButton';

import '../../css/pages/edit_user.css';
const EditUser = () => {

  const { auth, setAuth } = useAuth();
  const { showNotification } = useNotification();

  const [t] = useTranslation('global');

  const [, setCreatingNewUser] = useCreatingNewUserAtom();
  const [userToEdit, setUserToEdit] = useUserToEditAtom();

  const [showEditPassword, setShowEditPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showVerifyPassword, setShowVerifyPassword] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [deactivating, setDeactivating] = useState(false);

  // „Vymazanie“ používateľa = deaktivácia (dá sa obnoviť); vlastný účet sa deaktivovať nedá
  const isActive = userToEdit?.isActive !== false;
  const isSelf = userToEdit && String(auth?.uid) === String(userToEdit.id);
  // AD/SSO používateľ: heslo spravuje Active Directory -> zmena hesla v aplikácii sa neponúka
  const isAd = (userToEdit?.authMethod ?? userToEdit?.AuthMethod ?? '').toLowerCase() === 'ad';
  const userLabel = userToEdit
    ? ([userToEdit.userFirstname, userToEdit.userLastname].filter(Boolean).join(' ') || userToEdit.userEmail)
    : '';

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    newUserName: '',
    lastName: '',
    email: '',
    roleId : '',
    teamIds: [],
    password: '',
    verifyPassword: ''
  });

  const handleOpen = () => setOpen(true);

  const handleClose = () => setOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === 'password') {
      const passwordRegex = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()_+=<>,.?/|\-])[a-zA-Z0-9!@#$%^&*()_+=<>,.?/|\-]{8,}$/;
      setPasswordError(!passwordRegex.test(value));
    }
  };

  const handleTeamChange = (event) => {
    const {
        target: { value },
    } = event;
    setFormData({
        ...formData,
        teamIds: typeof value === 'string' ? value.split(',') : value,
    });
  };

  const handleShowEditPassword = () => {
    setShowEditPassword(true);
  }

  // Vygenerované heslo (spĺňa pravidlá) do oboch políčok + zobraziť, nech ho admin vidí
  const handleGeneratedPassword = (pwd) => {
    setFormData(prev => ({ ...prev, password: pwd, verifyPassword: pwd }));
    setPasswordError(false);
    setShowPassword(true);
    setShowVerifyPassword(true);
  }

  const handleGoBack = () => {
    setCreatingNewUser(false);
    navigate('/Users');
  };

  const handleSubmit = (e, action) => {
    e.preventDefault();
    console.log('Submitting:', formData);

    if (!showEditPassword) {
      setIsLoading(true);
      updateUser(userToEdit.id, formData.newUserName, formData.lastName, '', formData.roleId, formData.teamIds)
        .then((response) => {
          showNotification({ message: t('savedChanges'), type: NotificationType.Success});

          // response.teams nemusi prist z BE -> osetrime, aby ulozenie nespadlo a stranka neostala na "Nacitavam"
          const updatedSystemTeams = (response?.teams || [])
            .filter(team => team.teamType === 'S')
            .map(team => ({
              id: team.id,
              name: team.name
            }));

          setAuth(prev => ({
            ...prev,
            ust: updatedSystemTeams
          }));

          setUserToEdit(null);
          setIsLoading(false);
          navigate('/Users');
        })
        .catch((error) => {
          console.error(error);
          showNotification({ message: t('defaultEx'), type: NotificationType.Error });
          setIsLoading(false);
        });
    } else {
      const passwordRegex = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*()_+=<>,.?/|\-])[a-zA-Z0-9!@#$%^&*()_+=<>,.?/|\-]{8,}$/;

      if (!formData.password || formData.password.trim() === '') {
        showNotification({ message: t('password') + ' ' + t('isRequired'), type: NotificationType.Error });
        return;
      }

      if (formData.password.length < 8 || !passwordRegex.test(formData.password)) {
        showNotification({ message: t('pwdRule'), type: NotificationType.Error });
        return;
      }

      if (!formData.verifyPassword || formData.verifyPassword.trim() === '') {
        showNotification({ message: t('verifyPassword') + ' ' + t('isRequired'), type: NotificationType.Error });
        return;
      }

      if (formData.password !== formData.verifyPassword) {
        showNotification({ message: t('passwordsDontMatch'), type: NotificationType.Error });
        return;
      }

      setIsLoading(true);
      updateUser(userToEdit.id, formData.newUserName, formData.lastName, formData.password, formData.roleId, formData.teamIds)
      .then(r => {
        showNotification({ message: t('savedChanges'), type: NotificationType.Success});
        
        // doplnit ust update
        
        setUserToEdit(null);
        setIsLoading(false);
        navigate('/Users');
      }).catch(error => {
        setIsLoading(false);

        if (error.response?.status === 400) {
          showNotification({ message: t('pwdRule'), type: NotificationType.Error });
        }
      });
    }
  };

  const handleNo = () => {
    handleClose();
  }

  // Deaktivácia: okno ostane otvorené („Deaktivujem…“), kým backend neodpovie
  const handleYes = async () => {
    setDeactivating(true);
    try {
      await deleteUser(userToEdit.id);
      showNotification({ message: t('userDeactivated'), type: NotificationType.Success });
      setUserToEdit(null);
      navigate('/Users');
    } catch (error) {
      console.error(error);
      const detail = error?.response?.data?.detail;
      showNotification({ message: typeof detail === 'string' ? detail : t('defaultEx'), type: NotificationType.Error });
    } finally {
      setDeactivating(false);
      handleClose();
    }
  }

  const handleReactivate = () => {
    setIsLoading(true);
    reactivateUser(userToEdit.id)
      .then(() => {
        showNotification({ message: t('userReactivated'), type: NotificationType.Success });
        setUserToEdit({ ...userToEdit, isActive: true });   // ostaň na stránke, stav sa prepne na Aktívny
      })
      .catch((error) => {
        console.error(error);
        showNotification({ message: t('defaultEx'), type: NotificationType.Error });
      })
      .finally(() => setIsLoading(false));
  }

  useEffect(() => {
    if(userToEdit) {
      setFormData({
        newUserName: userToEdit.userFirstname || '',
        lastName: userToEdit.userLastname || '',
        email: userToEdit.userEmail,
        roleId: userToEdit.roleId,
        teamIds: userToEdit.teams.map(team => team.id),
        password: '',
        verifyPassword: '',
      }); 
    } else {
      navigate('/Users');
    }
  }, [userToEdit]);
  
  return (
    <div className="user_form page-wrap">
    <button className="return-button return-button-top" onClick={handleGoBack}>
      <span className="material-symbols-outlined">arrow_back</span>{t('return')}
    </button>
    <div className="header-title edit-user-title">
      <h1>{t('editUser')}</h1>
      {userToEdit && (
        <span className={`user-status-badge ${isActive ? 'active' : 'inactive'}`}>
          {isActive ? t('statusActive') : t('statusInactive')}
        </span>
      )}
    </div>

      <ConfirmDeleteModal
        open={open}
        onNo={handleNo}
        onYes={handleYes}
        onClose={handleClose}
        item={userLabel}
        question={t('deactivateQuestion')}
        warning={t('deactivateUserInfo')}
        confirmLabel={t('deactivate')}
        loadingLabel={t('deactivating')}
        loading={deactivating}
      />

      <form autoComplete="off" className="input-form">

        <div className="input-section">
          <div className="input-line">
            <label htmlFor="newUserName">{t('userName')}</label>
            <input
              type="text"
              id="newUserName"
              name="newUserName"
              value={formData.newUserName}
              onChange={handleChange}
              autoComplete="new-password"
            />
          </div>
          <div className="input-line">
            <label htmlFor="lastName">{t('lastName')}</label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              autoComplete="new-password"
            />
          </div>
        </div> 

        <div className="input-section">      
          <div className="input-line">
            <label htmlFor="email">{t('email')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              autoComplete="new-password"
            />
          </div>
          <div className='input-line'>
            <div id="role-selectbox" className="selectbox-section">
              <RoleSelectBox selectValue={formData.roleId} onChange={handleChange}/>
            </div>
          </div>
        </div>

          <div className='input-line'>
            <div className='selectbox-section'>
              <AllTeamsMultiselect selectedValues={formData.teamIds} onChange={handleTeamChange}/>
            </div>
          </div>

        {isAd ? (
            <p className="ad-password-note">
              <span className="material-symbols-outlined" aria-hidden="true">badge</span>
              {t('adPasswordNote')}
            </p>
          ) : !showEditPassword ? (
            <button className="option-button" type="button" onClick={handleShowEditPassword}>
              {t('showPasswordEdit')}
            </button>
          ) : (
            <div className="input-section">
             <div className="input-line">

              <div className='pwd-info'>
                <label htmlFor="password">{t('password')}
                </label>
                <span className="material-symbols-outlined pwd-tooltip">
                  info
                  <span className='tooltip-text'>{t('pwdRule')}</span>
                </span>
                <PasswordGenerateButton onGenerate={handleGeneratedPassword} />
              </div>

              {passwordError && formData.password.length > 0 && (
                <>
                  <p className="password-requirements">{t('pwdRule')}</p>
                  <p className="password-requirements-special">{t('pwdRequirements')}</p>
                </>
              )}

              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className={`password-toggle ${showPassword ? 'is-visible' : 'is-hidden'}`}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                />
              </div>
            </div>
            <div className="input-line">
              <label htmlFor="verifyPassword">{t('verifyPassword')}</label>
              <div className="password-input-wrapper">
                <input
                  type={showVerifyPassword ? "text" : "password"}
                  id="verifyPassword"
                  name="verifyPassword"
                  value={formData.verifyPassword}
                  onChange={handleChange}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className={`password-toggle ${showVerifyPassword ? 'is-visible' : 'is-hidden'}`}
                  onClick={() => setShowVerifyPassword(!showVerifyPassword)}
                  aria-label={showVerifyPassword ? 'Hide password' : 'Show password'}
                />
              </div>
            </div>
            </div>
          )}
        <div>{isLoading ? t('loading') : ''}</div>
      </form>

      <button className="submit-button" type="submit" onClick={(e) => handleSubmit(e, 'save')} disabled={isLoading}>{t('save')}</button>
      {isActive ? (
        !isSelf && (
          <button className="delete-button" type="button" onClick={handleOpen} disabled={isLoading}>{t('deactivateUser')}</button>
        )
      ) : (
        <button className="reactivate-button" type="button" onClick={handleReactivate} disabled={isLoading}>{t('reactivateUser')}</button>
      )}
   
    </div> 
  );
};

export default EditUser;
